import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  InputNumber,
  Button,
  Space,
  Typography,
  message,
  Spin,
  Row,
  Col,
  Tag,
} from 'antd';
import {
  SaveOutlined,
  TrophyOutlined,
  StarOutlined,
  GiftOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

interface SettingValue {
  value: number | string;
  raw: string;
  type: string;
  description: string | null;
  updated_by: number | null;
  updated_at: string;
}

const RewardSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/rewards/settings`);
      // Populate form with current values
      const formValues: Record<string, number> = {};
      for (const [key, setting] of Object.entries(response.data as Record<string, SettingValue>)) {
        formValues[key] = typeof setting.value === 'number' ? setting.value : Number(setting.raw);
      }
      form.setFieldsValue(formValues);
    } catch (error) {
      message.error('Failed to load reward settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: Record<string, number>) => {
    try {
      setSaving(true);
      const settingsPayload = Object.entries(values).map(([key, value]) => ({
        key,
        value: String(value),
      }));
      const response = await api.put(`/rewards/settings`, { settings: settingsPayload });
      // Backend returns 207 Multi-Status when only some keys saved. axios
      // resolves all 2xx, so surface the partial failure instead of a false success.
      const errors = (response.data?.errors as Array<any> | undefined) || [];
      if (response.status === 207 || errors.length > 0) {
        const failed = errors
          .map((e) => (typeof e === 'string' ? e : e?.key || e?.setting))
          .filter(Boolean);
        message.warning(
          failed.length
            ? `Some settings did not save: ${failed.join(', ')}`
            : 'Some settings did not save. Please review your values and try again.'
        );
      } else {
        message.success('Reward settings saved successfully');
      }
      fetchSettings();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to save reward settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <Card>
          <Space align="center" size={12}>
            <TrophyOutlined style={{ fontSize: 28, color: '#faad14' }} />
            <div>
              <Title level={3} style={{ margin: 0 }}>Reward Program Settings</Title>
              <Text type="secondary">Configure points earning rates, tier thresholds, and program features</Text>
            </div>
          </Space>
        </Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          disabled={!isOwner}
        >
          {/* Points Earning */}
          <Card
            title={<Space><StarOutlined style={{ color: '#1890ff' }} /> Points Earning</Space>}
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="points_per_dollar"
                  label="Base Points per Dollar"
                  tooltip="How many points Bronze-tier customers earn per $1 spent"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber min={1} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="points_value_ratio"
                  label="Points per $1 Credit"
                  tooltip="How many points needed to redeem $1 in credit"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber min={10} max={1000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="points_expiry_months"
                  label="Points Expiry (months)"
                  tooltip="Months of inactivity before points expire (0 = never expire)"
                >
                  <InputNumber min={0} max={120} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Tier Thresholds */}
          <Card
            title={<Space><TrophyOutlined style={{ color: '#faad14' }} /> Tier Thresholds &amp; Bonus Rates</Space>}
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ background: '#f0f0f0', borderColor: '#d9d9d9' }}>
                  <Tag color="default" style={{ marginBottom: 8 }}>Silver</Tag>
                  <Form.Item
                    name="silver_threshold"
                    label="Lifetime Points Required"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <InputNumber min={100} max={1000000} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="silver_bonus_rate"
                    label="Points per $1"
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
                  <Tag color="gold" style={{ marginBottom: 8 }}>Gold</Tag>
                  <Form.Item
                    name="gold_threshold"
                    label="Lifetime Points Required"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <InputNumber min={100} max={1000000} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="gold_bonus_rate"
                    label="Points per $1"
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ background: '#f0f5ff', borderColor: '#adc6ff' }}>
                  <Tag color="geekblue" style={{ marginBottom: 8 }}>Platinum</Tag>
                  <Form.Item
                    name="platinum_threshold"
                    label="Lifetime Points Required"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <InputNumber min={100} max={1000000} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="platinum_bonus_rate"
                    label="Points per $1"
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Card>
              </Col>
            </Row>
          </Card>

          {/* Referral Program */}
          <Card
            title={<Space><TeamOutlined style={{ color: '#52c41a' }} /> Referral Program</Space>}
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="referral_pairs_required"
                  label="Referrals per Reward"
                  tooltip="Number of referrals needed before earning bonus points"
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={20} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="referral_max_points"
                  label="Max Points per Referral Pair"
                  rules={[{ required: true }]}
                >
                  <InputNumber min={100} max={10000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Hero & DVI */}
          <Card
            title={<Space><GiftOutlined style={{ color: '#eb2f96' }} /> Hero Discount &amp; DVI Sharing</Space>}
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="hero_discount_percent"
                  label="Hero Labor Discount %"
                  tooltip="Percentage discount on labor for verified heroes (military, first responder, etc.)"
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={50} addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="dvi_share_click_threshold"
                  label="DVI Share Click Threshold"
                  tooltip="Number of clicks on shared DVI link before coupon is generated"
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={50} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="dvi_coupon_percent"
                  label="DVI Coupon Discount %"
                  tooltip="Percentage discount coupon earned from DVI social sharing"
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={50} addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {isOwner && (
            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                Save All Settings
              </Button>
            </div>
          )}

          {!isOwner && (
            <Card>
              <Text type="secondary">
                Only the shop owner can modify reward settings. Contact the owner to make changes.
              </Text>
            </Card>
          )}
        </Form>
      </Space>
    </div>
  );
};

export default RewardSettings;
